import { Point, Wall, StairsElement } from '../types';
import { distanceToLine } from './floorPlanUtils';
import { SNAP_THRESHOLD, WALL_THICKNESS, PIXELS_PER_METER } from '../constants/floorPlanConstants';

const DEFAULT_STAIRS_WIDTH = 80; // Default depth from wall (perpendicular) - 2x size
const DEFAULT_STAIRS_LENGTH = 180; // Default length along wall - 2x size
const STAIRS_WALL_OFFSET = 0; // No offset - stairs edge touches wall
const RESIZE_HANDLE_SIZE = 10; // Size of resize handles in pixels
const STAIRS_SNAP_THRESHOLD = 80; // Aggressive snapping - stays snapped within 80 pixels of wall
const MIN_SIDE_DISTANCE = 15; // Minimum distance from wall center to determine side - prevents on-wall snapping

function getClosestPointOnSegment(point: Point, start: Point, end: Point): Point {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) return start;
  const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy);
  const clampedT = Math.max(0, Math.min(1, t));
  return { x: start.x + clampedT * dx, y: start.y + clampedT * dy };
}

/**
 * Find the nearest wall to a point (for stairs placement)
 * Uses aggressive snapping threshold to keep stairs attached to walls
 * Only snaps if cursor is clearly on one side (not directly on the wall)
 */
export function findWallForStairs(point: Point, walls: (Wall & { tempId: string, stableId: string })[]): (Wall & { tempId: string, stableId: string }) | null {
  let clickedWall: (Wall & { tempId: string, stableId: string }) | null = null;
  let minDistance = Infinity;

  for (const wall of walls) {
    const distance = distanceToLine(point, wall.start, wall.end);
    
    // Only snap if within threshold AND not too close to wall center (prevents on-wall placement)
    if (distance < STAIRS_SNAP_THRESHOLD && distance >= MIN_SIDE_DISTANCE) {
      if (distance < minDistance) {
        minDistance = distance;
        clickedWall = wall;
      }
    }
  }
  return clickedWall;
}

/**
 * Create a stairs element with optional wall snapping
 * If no wall is found nearby, creates a free-floating stairs
 */
export function createStairsWithAutoSnap(
  point: Point,
  walls: (Wall & { tempId: string, stableId: string })[],
  floor: number,
  length: number = DEFAULT_STAIRS_LENGTH,
  width: number = DEFAULT_STAIRS_WIDTH,
  direction: 'up' | 'down' = 'up'
): StairsElement {
  // Try to find a nearby wall to snap to
  const nearbyWall = findWallForStairs(point, walls);
  
  if (nearbyWall) {
    // Snap to wall
    return createStairsElement(point, nearbyWall, floor, length, width, direction);
  } else {
    // Create free-floating stairs at cursor position
    const rotation = 0; // Default horizontal orientation
    const halfLength = length / 2;
    
    return {
      id: `stairs-${Date.now()}`,
      type: 'stairs',
      wallId: '', // No wall association for free-floating stairs
      start: { x: point.x - halfLength, y: point.y },
      end: { x: point.x + halfLength, y: point.y },
      width,
      length,
      floor,
      rotation,
      side: 1, // Default side
      direction,
    };
  }
}

/**
 * Create a stairs element snapped to a specific wall
 */
export function createStairsElement(
  point: Point,
  wall: Wall & { tempId: string, stableId: string },
  floor: number,
  length: number = DEFAULT_STAIRS_LENGTH,
  width: number = DEFAULT_STAIRS_WIDTH,
  direction: 'up' | 'down' = 'up'
): StairsElement {
  const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  
  // Snap point to wall
  const closestPointOnWall = getClosestPointOnSegment(point, wall.start, wall.end);
  const distFromStart = Math.hypot(closestPointOnWall.x - wall.start.x, closestPointOnWall.y - wall.start.y);
  
  // Calculate position along wall (normalized 0-1)
  let positionOnWall = distFromStart / wallLength;
  
  // Keep stairs within wall bounds
  const halfStairsLengthOnWall = (length / 2) / wallLength;
  positionOnWall = Math.max(halfStairsLengthOnWall, Math.min(positionOnWall, 1 - halfStairsLengthOnWall));
  
  // Calculate stairs center on wall
  const stairsCenterOnWall = {
    x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
    y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
  };
  
  // Calculate perpendicular offset from wall
  const wallDx = wall.end.x - wall.start.x;
  const wallDy = wall.end.y - wall.start.y;
  const wallAngle = Math.atan2(wallDy, wallDx);
  const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
  
  // Determine which side of the wall the click is on
  const toPoint = { x: point.x - closestPointOnWall.x, y: point.y - closestPointOnWall.y };
  const distanceFromWall = toPoint.x * perpVector.x + toPoint.y * perpVector.y;
  
  // Use sign of perpendicular distance to determine which side
  const side = distanceFromWall >= 0 ? 1 : -1;
  
  // Calculate start and end points along the wall
  const halfLength = length / 2;
  const startOnWall = {
    x: stairsCenterOnWall.x - (wallDx / wallLength) * halfLength,
    y: stairsCenterOnWall.y - (wallDy / wallLength) * halfLength,
  };
  const endOnWall = {
    x: stairsCenterOnWall.x + (wallDx / wallLength) * halfLength,
    y: stairsCenterOnWall.y + (wallDy / wallLength) * halfLength,
  };
  
  // Offset from wall to interior side - position the stairs edge exactly at wall surface
  const totalOffset = (WALL_THICKNESS / 2) * side;
  
  const start = {
    x: startOnWall.x + perpVector.x * totalOffset,
    y: startOnWall.y + perpVector.y * totalOffset,
  };
  
  const end = {
    x: endOnWall.x + perpVector.x * totalOffset,
    y: endOnWall.y + perpVector.y * totalOffset,
  };
  
  return {
    id: `stairs-${Date.now()}`,
    type: 'stairs',
    wallId: wall.stableId,
    start,
    end,
    width,
    length,
    floor,
    rotation: wallAngle,
    side,
    direction,
  };
}

/**
 * Draw stairs elements
 */
export function drawStairs(
  ctx: CanvasRenderingContext2D,
  elements: (StairsElement)[],
  currentFloor: number,
  scale: number,
  viewOffset: Point,
  isSelected?: (id: string) => boolean,
  showResizeHandles?: boolean,
  isEstate: boolean = false
) {
  const stairs = elements.filter(el => el.floor === currentFloor);
  
  stairs.forEach(stair => {
    ctx.save();
    
    // Draw rectangle from start to end with width
    const dx = stair.end.x - stair.start.x;
    const dy = stair.end.y - stair.start.y;
    const perpDx = -dy;
    const perpDy = dx;
    const perpLen = Math.hypot(perpDx, perpDy);
    const perpNormX = perpDx / perpLen;
    const perpNormY = perpDy / perpLen;
    
    // Calculate four corners of the stairs
    const corners = [
      { x: stair.start.x, y: stair.start.y },
      { x: stair.end.x, y: stair.end.y },
      { x: stair.end.x + perpNormX * stair.width, y: stair.end.y + perpNormY * stair.width },
      { x: stair.start.x + perpNormX * stair.width, y: stair.start.y + perpNormY * stair.width },
    ];
    
    const selected = isSelected && isSelected(stair.id);
    
    // 🏢 ESTATE MODE: Professional B&W design
    if (isEstate) {
      // Simple white fill
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      corners.forEach((corner, i) => {
        if (i > 0) ctx.lineTo(corner.x, corner.y);
      });
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Draw horizontal step lines (professional look)
      const numSteps = Math.max(5, Math.floor(stair.length / 15));
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.5;
      
      for (let i = 1; i < numSteps; i++) {
        const t = i / numSteps;
        const stepStartX = stair.start.x + dx * t;
        const stepStartY = stair.start.y + dy * t;
        const stepEndX = stepStartX + perpNormX * stair.width;
        const stepEndY = stepStartY + perpNormY * stair.width;
        
        ctx.beginPath();
        ctx.moveTo(stepStartX, stepStartY);
        ctx.lineTo(stepEndX, stepEndY);
        ctx.stroke();
      }
    } else {
      // Original colorful design for non-estate mode
      // Draw subtle shadow for depth
      ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      
      // Fill stairs with a gradient for visual depth
      const gradient = ctx.createLinearGradient(
        stair.start.x, stair.start.y,
        stair.start.x + perpNormX * stair.width, stair.start.y + perpNormY * stair.width
      );
      
      if (selected) {
        gradient.addColorStop(0, '#dbeafe');
        gradient.addColorStop(1, '#bfdbfe');
      } else {
        gradient.addColorStop(0, '#f9fafb');
        gradient.addColorStop(1, '#e5e7eb');
      }
      
      ctx.fillStyle = gradient;
      ctx.strokeStyle = selected ? '#3b82f6' : '#1f2937';
      ctx.lineWidth = selected ? 3 : 2;
      
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      corners.forEach((corner, i) => {
        if (i > 0) ctx.lineTo(corner.x, corner.y);
      });
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      
      // Draw stair steps (lines perpendicular to the length)
      const numSteps = Math.max(5, Math.floor(stair.length / 15));
      ctx.strokeStyle = '#9ca3af';
      ctx.lineWidth = 2;
      
      for (let i = 1; i < numSteps; i++) {
        const t = i / numSteps;
        const stepStartX = stair.start.x + dx * t;
        const stepStartY = stair.start.y + dy * t;
        const stepEndX = stepStartX + perpNormX * stair.width;
        const stepEndY = stepStartY + perpNormY * stair.width;
        
        ctx.beginPath();
        ctx.moveTo(stepStartX, stepStartY);
        ctx.lineTo(stepEndX, stepEndY);
        ctx.stroke();
      }
      
      // Draw direction arrow with background circle
      const centerX = (stair.start.x + stair.end.x) / 2 + perpNormX * stair.width / 2;
      const centerY = (stair.start.y + stair.end.y) / 2 + perpNormY * stair.width / 2;
      const arrowLength = Math.min(stair.width, stair.length) * 0.45;
      
      // Draw background circle for arrow
      const circleRadius = Math.min(stair.width, stair.length) * 0.25;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.strokeStyle = stair.direction === 'up' ? '#10b981' : '#ef4444';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      
      ctx.strokeStyle = stair.direction === 'up' ? '#10b981' : '#ef4444';
      ctx.fillStyle = stair.direction === 'up' ? '#10b981' : '#ef4444';
      ctx.lineWidth = 3.5;
      
      // Arrow along length direction
      const arrowDx = (stair.end.x - stair.start.x) / Math.hypot(dx, dy);
      const arrowDy = (stair.end.y - stair.start.y) / Math.hypot(dx, dy);
      
      const arrowStartX = centerX - arrowDx * arrowLength / 2;
      const arrowStartY = centerY - arrowDy * arrowLength / 2;
      const arrowEndX = centerX + arrowDx * arrowLength / 2;
      const arrowEndY = centerY + arrowDy * arrowLength / 2;
      
      // Draw arrow line
      ctx.beginPath();
      ctx.moveTo(arrowStartX, arrowStartY);
      ctx.lineTo(arrowEndX, arrowEndY);
      ctx.stroke();
      
      // Draw arrow head
      const headSize = 10;
      const angle = Math.atan2(arrowDy, arrowDx);
      ctx.beginPath();
      ctx.moveTo(arrowEndX, arrowEndY);
      ctx.lineTo(
        arrowEndX - headSize * Math.cos(angle - Math.PI / 6),
        arrowEndY - headSize * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        arrowEndX - headSize * Math.cos(angle + Math.PI / 6),
        arrowEndY - headSize * Math.sin(angle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fill();
    }
    
    // Draw resize handles if stairs tool is active or stairs is selected
    if (selected || showResizeHandles) {
      const handleSize = 12 / scale;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3 / scale;
      
      // Corner handles for length adjustment (blue)
      ctx.fillStyle = '#3b82f6';
      [corners[0], corners[1]].forEach((corner, idx) => {
        ctx.beginPath();
        ctx.arc(corner.x, corner.y, handleSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        
        // Add "L" label for Length adjustment
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${handleSize * 1.2 / scale}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('L', corner.x, corner.y);
      });
      
      // Corner handles for width adjustment (orange)
      ctx.fillStyle = '#f59e0b';
      [corners[2], corners[3]].forEach((corner, idx) => {
        ctx.beginPath();
        ctx.arc(corner.x, corner.y, handleSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        
        // Add "W" label for Width adjustment
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${handleSize * 1.2 / scale}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('W', corner.x, corner.y);
      });
      
      // Move/drag control at center
      const bottomMidX = (corners[0].x + corners[1].x) / 2;
      const bottomMidY = (corners[0].y + corners[1].y) / 2;
      
      ctx.fillStyle = '#f59e0b'; // Orange for move
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2 / scale;
      ctx.beginPath();
      ctx.arc(bottomMidX, bottomMidY, handleSize * 1.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      
      // Add move icon (4-way arrows)
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5 / scale;
      const arrowSize = handleSize * 0.5;
      // Vertical line
      ctx.beginPath();
      ctx.moveTo(bottomMidX, bottomMidY - arrowSize);
      ctx.lineTo(bottomMidX, bottomMidY + arrowSize);
      ctx.stroke();
      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(bottomMidX - arrowSize, bottomMidY);
      ctx.lineTo(bottomMidX + arrowSize, bottomMidY);
      ctx.stroke();
      
      // For free-floating stairs, add rotation control
      if (!stair.wallId) {
        const topMidX = (corners[2].x + corners[3].x) / 2;
        const topMidY = (corners[2].y + corners[3].y) / 2;
        
        ctx.fillStyle = '#10b981'; // Green for rotation
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 / scale;
        ctx.beginPath();
        ctx.arc(topMidX, topMidY, handleSize * 1.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        
        // Add rotation icon (circular arrow)
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5 / scale;
        const rotSize = handleSize * 0.6;
        ctx.beginPath();
        ctx.arc(topMidX, topMidY, rotSize, -Math.PI / 2, Math.PI, false);
        ctx.stroke();
        // Arrow head
        ctx.beginPath();
        ctx.moveTo(topMidX - rotSize, topMidY);
        ctx.lineTo(topMidX - rotSize - 3 / scale, topMidY - 3 / scale);
        ctx.lineTo(topMidX - rotSize + 3 / scale, topMidY - 3 / scale);
        ctx.closePath();
        ctx.fillStyle = '#ffffff';
        ctx.fill();
      }
    }
    
    ctx.restore();
  });
}

/**
 * Draw stairs preview during placement
 */
export function drawStairsPreview(
  ctx: CanvasRenderingContext2D,
  preview: StairsElement | null,
  scale: number
) {
  if (!preview) return;
  
  ctx.save();
  
  const dx = preview.end.x - preview.start.x;
  const dy = preview.end.y - preview.start.y;
  const perpDx = -dy;
  const perpDy = dx;
  const perpLen = Math.hypot(perpDx, perpDy);
  const perpNormX = perpDx / perpLen;
  const perpNormY = perpDy / perpLen;
  
  const corners = [
    { x: preview.start.x, y: preview.start.y },
    { x: preview.end.x, y: preview.end.y },
    { x: preview.end.x + perpNormX * preview.width, y: preview.end.y + perpNormY * preview.width },
    { x: preview.start.x + perpNormX * preview.width, y: preview.start.y + perpNormY * preview.width },
  ];
  
  // Draw semi-transparent filled rectangle
  ctx.globalAlpha = 0.5;
  ctx.fillStyle = preview.wallId ? '#a78bfa' : '#dbeafe'; // Purple if snapped, blue if free-floating
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  corners.forEach((corner, i) => {
    if (i > 0) ctx.lineTo(corner.x, corner.y);
  });
  ctx.closePath();
  ctx.fill();
  
  // Draw border with dashed line - thicker and more visible
  ctx.globalAlpha = 0.95;
  ctx.strokeStyle = preview.wallId ? '#7c3aed' : '#3b82f6'; // Purple if snapped, blue if free-floating
  ctx.lineWidth = 4;
  ctx.setLineDash([10, 5]);
  ctx.stroke();
  ctx.setLineDash([]);
  
  // If snapped to wall, draw connection indicator
  if (preview.wallId) {
    ctx.globalAlpha = 0.8;
    ctx.strokeStyle = '#8b5cf6';
    ctx.lineWidth = 3;
    
    // Draw attachment line along the wall edge (start-end edge)
    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    ctx.lineTo(corners[1].x, corners[1].y);
    ctx.stroke();
    
    // Draw snap indicator dots at corners
    ctx.fillStyle = '#7c3aed';
    [corners[0], corners[1]].forEach(corner => {
      ctx.beginPath();
      ctx.arc(corner.x, corner.y, 5, 0, Math.PI * 2);
      ctx.fill();
    });
  }
  
  // Draw stair steps
  ctx.globalAlpha = 0.5;
  const numSteps = Math.max(5, Math.floor(preview.length / 15));
  ctx.strokeStyle = '#4b5563';
  ctx.lineWidth = 2;
  
  for (let i = 1; i < numSteps; i++) {
    const t = i / numSteps;
    const stepStartX = preview.start.x + dx * t;
    const stepStartY = preview.start.y + dy * t;
    const stepEndX = stepStartX + perpNormX * preview.width;
    const stepEndY = stepStartY + perpNormY * preview.width;
    
    ctx.beginPath();
    ctx.moveTo(stepStartX, stepStartY);
    ctx.lineTo(stepEndX, stepEndY);
    ctx.stroke();
  }
  
  // Draw direction arrow prominently
  ctx.globalAlpha = 0.9;
  const centerX = (preview.start.x + preview.end.x) / 2 + perpNormX * preview.width / 2;
  const centerY = (preview.start.y + preview.end.y) / 2 + perpNormY * preview.width / 2;
  const arrowLength = Math.min(preview.width, preview.length) * 0.5;
  
  ctx.strokeStyle = preview.direction === 'up' ? '#10b981' : '#ef4444'; // Green for up, red for down
  ctx.fillStyle = preview.direction === 'up' ? '#10b981' : '#ef4444';
  ctx.lineWidth = 4;
  
  // Arrow along length direction
  const arrowDx = (preview.end.x - preview.start.x) / Math.hypot(dx, dy);
  const arrowDy = (preview.end.y - preview.start.y) / Math.hypot(dx, dy);
  
  const arrowStartX = centerX - arrowDx * arrowLength / 2;
  const arrowStartY = centerY - arrowDy * arrowLength / 2;
  const arrowEndX = centerX + arrowDx * arrowLength / 2;
  const arrowEndY = centerY + arrowDy * arrowLength / 2;
  
  // Draw arrow line
  ctx.beginPath();
  ctx.moveTo(arrowStartX, arrowStartY);
  ctx.lineTo(arrowEndX, arrowEndY);
  ctx.stroke();
  
  // Draw arrow head
  const headSize = 12;
  const angle = Math.atan2(arrowDy, arrowDx);
  ctx.beginPath();
  ctx.moveTo(arrowEndX, arrowEndY);
  ctx.lineTo(
    arrowEndX - headSize * Math.cos(angle - Math.PI / 6),
    arrowEndY - headSize * Math.sin(angle - Math.PI / 6)
  );
  ctx.lineTo(
    arrowEndX - headSize * Math.cos(angle + Math.PI / 6),
    arrowEndY - headSize * Math.sin(angle + Math.PI / 6)
  );
  ctx.closePath();
  ctx.fill();
  
  ctx.restore();
}

/**
 * Check if a point is inside stairs (for selection/resizing)
 */
export function isPointInsideStairs(point: Point, stairs: StairsElement): boolean {
  const dx = stairs.end.x - stairs.start.x;
  const dy = stairs.end.y - stairs.start.y;
  const perpDx = -dy;
  const perpDy = dx;
  const perpLen = Math.hypot(perpDx, perpDy);
  const perpNormX = perpDx / perpLen;
  const perpNormY = perpDy / perpLen;
  
  const corners = [
    { x: stairs.start.x, y: stairs.start.y },
    { x: stairs.end.x, y: stairs.end.y },
    { x: stairs.end.x + perpNormX * stairs.width, y: stairs.end.y + perpNormY * stairs.width },
    { x: stairs.start.x + perpNormX * stairs.width, y: stairs.start.y + perpNormY * stairs.width },
  ];
  
  // Point-in-polygon test
  let inside = false;
  for (let i = 0, j = corners.length - 1; i < corners.length; j = i++) {
    const xi = corners[i].x, yi = corners[i].y;
    const xj = corners[j].x, yj = corners[j].y;
    
    const intersect = ((yi > point.y) !== (yj > point.y))
        && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  
  return inside;
}

/**
 * Get resize handle at a point for stairs
 */
export function getStairsResizeHandle(
  point: Point,
  stairs: StairsElement,
  tolerance: number = 12
): 'length-start' | 'length-end' | 'width-start' | 'width-end' | null {
  const dx = stairs.end.x - stairs.start.x;
  const dy = stairs.end.y - stairs.start.y;
  const perpDx = -dy;
  const perpDy = dx;
  const perpLen = Math.hypot(perpDx, perpDy);
  const perpNormX = perpDx / perpLen;
  const perpNormY = perpDy / perpLen;
  
  const corners = [
    { x: stairs.start.x, y: stairs.start.y, handle: 'length-start' as const },
    { x: stairs.end.x, y: stairs.end.y, handle: 'length-end' as const },
    { x: stairs.end.x + perpNormX * stairs.width, y: stairs.end.y + perpNormY * stairs.width, handle: 'width-end' as const },
    { x: stairs.start.x + perpNormX * stairs.width, y: stairs.start.y + perpNormY * stairs.width, handle: 'width-start' as const },
  ];
  
  for (const corner of corners) {
    const dist = Math.hypot(point.x - corner.x, point.y - corner.y);
    if (dist < tolerance) {
      return corner.handle;
    }
  }
  
  return null;
}

/**
 * Resize stairs by handle
 */
export function resizeStairs(
  stairs: StairsElement,
  handle: 'length-start' | 'length-end' | 'width-start' | 'width-end',
  point: Point,
  wall?: Wall & { tempId: string; stableId: string }
): StairsElement {
  const resized = { ...stairs };
  
  if (handle === 'length-start' || handle === 'length-end') {
    // Adjust length along wall or freely
    if (wall && stairs.wallId === wall.stableId) {
      // Wall-snapped: constrain to wall
      const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      const closestPointOnWall = getClosestPointOnSegment(point, wall.start, wall.end);
      const distFromWallStart = Math.hypot(closestPointOnWall.x - wall.start.x, closestPointOnWall.y - wall.start.y);
      const positionOnWall = distFromWallStart / wallLength;
      
      if (handle === 'length-start') {
        // Move start, keep end fixed
        const endDistFromStart = Math.hypot(stairs.end.x - wall.start.x, stairs.end.y - wall.start.y);
        const endPos = endDistFromStart / wallLength;
        
        const newStartPos = Math.max(0, Math.min(positionOnWall, endPos - 0.05));
        const newLength = (endPos - newStartPos) * wallLength;
        resized.length = newLength;
        
        const wallDx = wall.end.x - wall.start.x;
        const wallDy = wall.end.y - wall.start.y;
        const wallAngle = Math.atan2(wallDy, wallDx);
        const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
        const totalOffset = (WALL_THICKNESS / 2) * stairs.side;
        
        resized.start = {
          x: wall.start.x + (wallDx / wallLength) * newStartPos * wallLength + perpVector.x * totalOffset,
          y: wall.start.y + (wallDy / wallLength) * newStartPos * wallLength + perpVector.y * totalOffset,
        };
      } else {
        // Move end, keep start fixed
        const startDistFromStart = Math.hypot(stairs.start.x - wall.start.x, stairs.start.y - wall.start.y);
        const startPos = startDistFromStart / wallLength;
        
        const newEndPos = Math.max(startPos + 0.05, Math.min(positionOnWall, 1));
        const newLength = (newEndPos - startPos) * wallLength;
        resized.length = newLength;
        
        const wallDx = wall.end.x - wall.start.x;
        const wallDy = wall.end.y - wall.start.y;
        const wallAngle = Math.atan2(wallDy, wallDx);
        const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
        const totalOffset = (WALL_THICKNESS / 2) * stairs.side;
        
        resized.end = {
          x: wall.start.x + (wallDx / wallLength) * newEndPos * wallLength + perpVector.x * totalOffset,
          y: wall.start.y + (wallDy / wallLength) * newEndPos * wallLength + perpVector.y * totalOffset,
        };
      }
    }
  } else {
    // Adjust width perpendicular to length
    const dx = stairs.end.x - stairs.start.x;
    const dy = stairs.end.y - stairs.start.y;
    const perpX = -dy;
    const perpY = dx;
    const perpLen = Math.hypot(perpX, perpY);
    const perpNormX = perpX / perpLen;
    const perpNormY = perpY / perpLen;
    
    const refPoint = handle === 'width-start' ? stairs.start : stairs.end;
    const toPoint = { x: point.x - refPoint.x, y: point.y - refPoint.y };
    const perpDist = toPoint.x * perpNormX + toPoint.y * perpNormY;
    
    resized.width = Math.max(20, Math.abs(perpDist));
  }
  
  return resized;
}

export function isPointOnStairsRotationControl(point: Point, stairs: StairsElement, tolerance: number = 12): boolean {
  if (stairs.wallId) return false; // Only for free-floating stairs
  
  const dx = stairs.end.x - stairs.start.x;
  const dy = stairs.end.y - stairs.start.y;
  const perpX = -dy;
  const perpY = dx;
  const perpLen = Math.hypot(perpX, perpY);
  const perpNormX = perpX / perpLen;
  const perpNormY = perpY / perpLen;
  
  const topMidX = (stairs.end.x + perpNormX * stairs.width + stairs.start.x + perpNormX * stairs.width) / 2;
  const topMidY = (stairs.end.y + perpNormY * stairs.width + stairs.start.y + perpNormY * stairs.width) / 2;
  
  const dist = Math.hypot(point.x - topMidX, point.y - topMidY);
  return dist < tolerance;
}

export function isPointOnStairsMoveControl(point: Point, stairs: StairsElement, tolerance: number = 12): boolean {
  const bottomMidX = (stairs.start.x + stairs.end.x) / 2;
  const bottomMidY = (stairs.start.y + stairs.end.y) / 2;
  
  const dist = Math.hypot(point.x - bottomMidX, point.y - bottomMidY);
  return dist < tolerance;
}

/**
 * Drag a wall-snapped stairs along the wall
 */
export function dragStairsAlongWall(
  stairs: StairsElement,
  point: Point,
  wall: Wall & { tempId: string, stableId: string }
): StairsElement {
  const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
  const wallDx = wall.end.x - wall.start.x;
  const wallDy = wall.end.y - wall.start.y;
  const wallAngle = Math.atan2(wallDy, wallDx);
  const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
  
  // Project cursor point onto the wall
  const closestPointOnWall = getClosestPointOnSegment(point, wall.start, wall.end);
  const distFromWallStart = Math.hypot(closestPointOnWall.x - wall.start.x, closestPointOnWall.y - wall.start.y);
  
  // Calculate new position along wall (normalized 0-1)
  let positionOnWall = distFromWallStart / wallLength;
  
  // Keep stairs within wall bounds
  const halfStairsLengthOnWall = (stairs.length / 2) / wallLength;
  positionOnWall = Math.max(halfStairsLengthOnWall, Math.min(positionOnWall, 1 - halfStairsLengthOnWall));
  
  // Calculate new stairs center on wall
  const stairsCenterOnWall = {
    x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
    y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
  };
  
  // Calculate new start and end points along the wall
  const halfLength = stairs.length / 2;
  const startOnWall = {
    x: stairsCenterOnWall.x - (wallDx / wallLength) * halfLength,
    y: stairsCenterOnWall.y - (wallDy / wallLength) * halfLength,
  };
  const endOnWall = {
    x: stairsCenterOnWall.x + (wallDx / wallLength) * halfLength,
    y: stairsCenterOnWall.y + (wallDy / wallLength) * halfLength,
  };
  
  // Apply wall edge offset
  const wallEdgeOffset = (WALL_THICKNESS / 2) * stairs.side;
  
  const newStart = {
    x: startOnWall.x + perpVector.x * wallEdgeOffset,
    y: startOnWall.y + perpVector.y * wallEdgeOffset,
  };
  
  const newEnd = {
    x: endOnWall.x + perpVector.x * wallEdgeOffset,
    y: endOnWall.y + perpVector.y * wallEdgeOffset,
  };
  
  return {
    ...stairs,
    start: newStart,
    end: newEnd,
  };
}
